import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { redirectTargetFrom, type RedirectFromState } from '@/types/authNavigation';

interface PublicRouteProps {
  children: React.ReactNode;
}

/**
 * Route wrapper for public/auth pages.
 *
 * - Shows loading screen while checking auth status
 * - Redirects authenticated users to their intended destination or dashboard
 * - Allows unauthenticated users to access auth pages (login, register, etc.)
 *
 * @example
 * ```tsx
 * <PublicRoute>
 *   <LoginPage />
 * </PublicRoute>
 * ```
 */
export function PublicRoute({ children }: PublicRouteProps): React.ReactElement {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading screen while determining auth status
  if (isLoading) {
    return <LoadingScreen />;
  }

  // If authenticated, redirect to the intended destination or dashboard.
  // Exception: /auth/verify stays mounted after login() because it may need to
  // show the TrustDevicePrompt or PinSetupPrompt before navigating away.
  if (isAuthenticated && !location.pathname.startsWith('/auth/verify') && !location.pathname.startsWith('/auth/passkey') && !location.pathname.startsWith('/auth/pin')) {
    // Same helper the sign-in pages use, so one stored location cannot mean two
    // different destinations. It also keeps the query string and refuses an auth path,
    // which a bare `pathname` read did not.
    const state = location.state as RedirectFromState | null;

    return <Navigate to={redirectTargetFrom(state?.from)} replace />;
  }

  // User is not authenticated, render auth pages
  return <>{children}</>;
}
